import {Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, Renderer2, SimpleChanges, ViewChild} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {DraggingService} from '@application/services/dragging/dragging.service';
import {FixedShape} from '@domain/drawing-library/fixed-shape.enum';
import {IdName} from '@domain/id-name';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleDrawingViewMode} from '@domain/production-schedule/production-schedule-drawing-view-mode.enum';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {PropertyValue} from '@domain/property-value';
import {TargetForListOfDrawingsEnum} from '@domain/target-for-list-of-drawings.enum';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {PathWidth} from '@domain/textile-data/machine-quality/path-width';
import {StatusBarTarget} from '@domain/textile-data/status-bar-target.constants';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {DRAWINGS, Drawings} from '@infrastructure/http/drawing/drawings';
import {HttpStatusBarService} from '@infrastructure/http/status-bar/http-status-bar.service';
import {OrderlineSettingsData} from '@presentation/components/dialog-button/dialog-data/order-lines-settings/order-lines-setting-data.interface';
import {OrderLinesCustomSettingsDialogComponent} from '@presentation/components/dialog-button/dialog-data/order-lines-settings/order-lines-settings.component';
import {ListDrawingConfiguration} from '@presentation/components/drawing-list/list-drawing-configuration';
import {DesignPreviewComponent} from '@presentation/components/grid/design-preview/design-preview.component';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {GridCreelPreviewOfCreelPositionComponent} from '@presentation/pages/textile-data/creel/add/grid-creel-preview-of-creel-position/grid-creel-preview-of-creel-position.component';
import {PreviewType} from '@presentation/pages/textile-data/creel/add/preview-type';
import {
  AgGridUtils,
  AssertionUtils,
  BaseComponent,
  ColDefBuilderFactoryService,
  convertPixelsToHeightInMeter,
  DialogBuilder,
  DialogBuilderFactoryService,
  DialogType,
  EmptyCellRendererComponent,
  GridModel,
  GridOptionsBuilder,
  GridOptionsBuilderFactoryService,
  HeaderClass,
  InputChipCellRendererComponent,
  InputChipCellRendererParams,
  LocalStorageService,
  NavigationHelperPrototypeService,
  NoDataOverlayComponentParams,
  NumberCellEditorComponent,
  NumberCellEditorParams,
  OverlayActionsService,
  OverlayComponentParamsAction,
  ProgressCellRendererComponent,
  StatusBar,
  StringUtils,
  TranslateService,
  Unit,
  UuidUtils
} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {
  CellClickedEvent,
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  CellRendererSelectorResult,
  CellValueChangedEvent,
  ColDef,
  Column,
  ColumnVisibleEvent,
  EditableCallbackParams,
  FilterChangedEvent,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ITooltipParams,
  RowDoubleClickedEvent,
  RowNode,
  RowSelectedEvent,
  SortModelItem,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams
} from 'ag-grid-community';
import {L10nIntlService} from 'angular-l10n';
import {cloneDeep} from 'lodash-es';
import {Observable, of, takeUntil} from 'rxjs';
import {DrawingLibraryDetailsForProductionScheduleData} from '../drawing-library-details/drawing-library-details-for-production-schedule-data.interface';
import {DrawingLibraryDetailsComponent} from '../drawing-library-details/drawing-library-details.component';
import {DrawingLibraryDetailsModule} from '../drawing-library-details/drawing-library-details.module';
import {DrawingLibraryHelper} from './drawing-library-helper';

@Component({
  selector: 'app-drawing-library-listview',
  templateUrl: './drawing-library-listview.component.html',
  styleUrls: ['./drawing-library-listview.component.scss']
})
export class DrawingLibraryListViewComponent extends BaseComponent implements OnInit, OnChanges {
  private readonly SLICE_SIZE = 30;
  private readonly ROW_HEIGHT_WITH_PREVIEW = 96;
  private readonly ROW_HEIGHT_WITHOUT_PREVIEW = 24;
  private readonly HEADER_HEIGHT_WITH_PREVIEW = 56;
  private readonly HEADER_HEIGHT_WITHOUT_PREVIEW = 32;
  private readonly DESIGN_NAME_TRANSLATION_KEY = 'DESIGN_LIBRARY.DETAILS.NAME';
  private readonly QUANTITY_TRANSLATION_KEY = 'ORDERBOOK.QUANTITY';
  private readonly noOverlayAction: OverlayComponentParamsAction[] = [
    {
      key: 'addEventRule',
      titleKey: this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.PRESELECTION.PRESELECTION'),
      isPrimary: true,
      disabled: false
    }
  ];

  @ViewChild('drawingsGrid') public drawingsGrid: AgGridAngular;
  @ViewChild('exportDrawingsGrid') public exportDrawingsGrid: AgGridAngular;

  @Input() public showSelectedItems = false;
  @Input() public advancedSearchFilters: PropertyValue[] = [];
  @Input() public isProductionSchedule = false;
  @Input() public showConfigurations = false;
  @Input() public productionScheduleItemMultiplier: number;
  @Input() public isExporting = false;
  @Input() public hideMissingDesigns = false;
  @Input() public filterOnDrawingType: DrawingType = null;
  @Input() public isPreSelection = false;
  @Input() public selectedPath: ProductionSchedulePath;
  @Input() public selectedRestPathColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet;
  @Input() public pathWidths: PathWidth[] = [];
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public availableSpaceInBuggyInPicks: number;
  @Input() public buggyLengthMaxInPicks: number;
  @Input() public preselectedDesignIds: number[] = [];
  @Input() public preselectedConfigurationIds: number[] = [];
  @Input() public selectedPathContentRowData: ProductionScheduleItemInPathGroup;
  @Output() public drawingIdSelected: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public configurationIdSelected: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() public drawingSelected: EventEmitter<ListDrawingConfiguration[]> = new EventEmitter<ListDrawingConfiguration[]>();
  @Output() public exportComplete: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public advancedSearchFiltersChange: EventEmitter<PropertyValue[]> = new EventEmitter<PropertyValue[]>();
  @Output() public selectedViewModeChanged = new EventEmitter<ProductionScheduleDrawingViewMode>();
  @Output() public navigateBackOutput = new EventEmitter<any>();
  @Output() public selectedPathOutput: EventEmitter<ProductionSchedulePath> = new EventEmitter<ProductionSchedulePath>();
  @Output() public selectedRestPathColoredYarnSetOutput: EventEmitter<OverviewListColoredYarnSetWithStartDent> = new EventEmitter<OverviewListColoredYarnSetWithStartDent>();

  public drawingListGridModel: GridModel;
  public gridOptionsForDrawings: GridOptions;
  public exportGridOptions: GridOptions = {};
  public exportData: Drawing[] = [];
  public statusBar: StatusBar;
  public readonly customSettingsComponent = OrderLinesCustomSettingsDialogComponent;
  public viewModes: ProductionScheduleDrawingViewMode[] = [
    ProductionScheduleDrawingViewMode.DESIGNS_LIST,
    ProductionScheduleDrawingViewMode.ORDER_LINES,
    ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES,
    ProductionScheduleDrawingViewMode.GROUPED_DESIGNS
  ];

  public selectedViewMode = ProductionScheduleDrawingViewMode.DESIGNS_LIST;
  public showOnlyPreSelectedDrawings = false;

  private dialogBuilder: DialogBuilder;
  private designPreviewFiltered: boolean;
  private floatingFilters: PropertyValue[] = [];
  private addingItemToPath = false;
  private processRowSelection: boolean = true;
  private hasQuantitySuggestionPermission: boolean = false;
  private readyToFetchRows: boolean = false;
  private readonly rollLengthColumnId = 'rollLength';
  private changingFilter: boolean = false;

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    @Optional() @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    @Inject(DRAWINGS) private readonly drawings: Drawings,
    private readonly colDefBuilderFactory: ColDefBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly gridOptionsBuilderFactoryService: GridOptionsBuilderFactoryService,
    private readonly l10nIntlService: L10nIntlService,
    private readonly renderer: Renderer2,
    private readonly draggingService: DraggingService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly statusBarService: HttpStatusBarService,
    private readonly overlayActionsService: OverlayActionsService,
    private readonly localStorage: LocalStorageService,
    @Optional() private readonly drawingLibraryHelper: DrawingLibraryHelper,
    private readonly navigationHelper: NavigationHelperPrototypeService
  ) {
    super();
  }

  public ngOnInit(): void {
    const currentSubscription = this.authentication.getCurrentSubscription();

    this.filterViewModes(currentSubscription);
    this.instantiateGridOptionsForDrawings();
    this.subscribeToOverlayActions();

    if (!this.isPreSelection) {
      this.viewModes.push(ProductionScheduleDrawingViewMode.GROUPED_DESIGN_THUMBNAILS);
    }

    this.hasQuantitySuggestionPermission = currentSubscription.hasPermission(Permission.TEXFAB_QUANTITY_SUGGESTION);

    this.selectedViewMode = this.localStorage.get('drawingLibraryViewMode') || ProductionScheduleDrawingViewMode.DESIGNS_LIST;

    if ((this.isPreSelection && this.selectedViewMode === ProductionScheduleDrawingViewMode.GROUPED_DESIGN_THUMBNAILS) || !this.viewModes.includes(this.selectedViewMode)) {
      this.selectedViewMode = ProductionScheduleDrawingViewMode.DESIGNS_LIST;
      this.selectedViewModeChanged.emit(this.selectedViewMode);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!AssertionUtils.isEmpty(this.preselectedDesignIds) && !this.isPreSelection && 'preselectedDesignIds' in changes) {
      this.showOnlyPreSelectedDrawings = true;
    }

    if (!this.isPreSelection) {
      this.setInitializationFilters();
    }
    if (this.isExporting) {
      this.exportAllData();
    }

    if (
      this.hasQuantitySuggestionPermission &&
      ((this.isProductionSchedule && 'productionSchedule' in changes) || ('selectedPath' in changes && !AssertionUtils.isNullOrUndefined(this.drawingsGrid?.api)))
    ) {
      this.drawingsGrid?.api?.forEachNode((rowNode: RowNode<ListDrawingConfiguration>) => {
        if (!AssertionUtils.isNullOrUndefined(rowNode.data)) {
          const value = this.drawingLibraryHelper.getQuantitySuggestionForPath(
            rowNode.data,
            this.productionSchedule,
            this.selectedPath,
            this.availableSpaceInBuggyInPicks,
            this.buggyLengthMaxInPicks,
            this.hasQuantitySuggestionPermission,
            this.productionScheduleItemMultiplier
          );
          rowNode.setDataValue('quantity', value);
        }
      });
    }
  }

  public updateGrid(gridModel: GridModel): void {
    this.drawingsGrid.api.setFilterModel(gridModel.filterModel);
  }

  public onCustomSettingsChanged(settings: {showPreselectedItems: boolean}): void {
    this.showOnlyPreSelectedDrawings = settings.showPreselectedItems ?? this.showOnlyPreSelectedDrawings;
    this.setInitializationFilters();
  }

  public getCustomSettingsDialogData(): any {
    return {
      showPreselectedItems: this.showOnlyPreSelectedDrawings,
      viewMode: this.selectedViewMode
    } as OrderlineSettingsData;
  }

  public getSelectedRows(): number {
    return !AssertionUtils.isNullOrUndefined(this.preselectedDesignIds) ? this.preselectedDesignIds.length : 0;
  }

  public onSelectedViewModeChanged(selectedViewMode: ProductionScheduleDrawingViewMode): void {
    this.localStorage.set('drawingLibraryViewMode', selectedViewMode);

    this.selectedViewModeChanged.emit(selectedViewMode);
  }

  public onDrawingCanceled(): void {
    if (!AssertionUtils.isNullOrUndefined(this.drawingsGrid?.api)) {
      this.drawingsGrid.api.deselectAll();
    }
  }

  public onDrawingDeleted(deletedDrawings: Drawing[]): void {
    this.onDrawingCanceled();
    if (!AssertionUtils.isNullOrUndefined(this.drawingsGrid) && !AssertionUtils.isNullOrUndefined(this.drawingsGrid.api)) {
      this.drawingsGrid.api.refreshServerSide();
    }
  }

  public onDrawingUpdated(updatedDrawing: Drawing): void {
    if (this.showConfigurations) {
      let drawingConfigurationCount = 0;
      this.drawingsGrid.api.forEachNode((node: RowNode) => {
        if (node.data.drawing.id === updatedDrawing.id) {
          const data = node.data;
          data.drawing = updatedDrawing;
          node.setData(data);
          ++drawingConfigurationCount;
        }
      });
      if (drawingConfigurationCount !== updatedDrawing.configurations.length) {
        this.drawingsGrid.api.refreshServerSide();
      }
    } else {
      this.drawingsGrid.api.forEachNode((node: RowNode) => {
        if (node.data.id === updatedDrawing.id) {
          node.setData(updatedDrawing);
        }
      });
    }
  }

  public exportAllData(): void {
    this.dialogBuilder = this.dialogBuilderFactoryService.getBuilder();
    this.dialogBuilder.openAlertDialog({
      titleText: 'AGGRID.EXPORT',
      messageText: 'AGGRID.EXPORTMESSAGE',
      type: DialogType.INFORMATION
    });

    const colDef = this.createColumnsDefinition();
    this.exportDrawingsGrid.api.setGridOption('columnDefs', colDef);
    const sortModel: SortModelItem[] = [];
    let startRow = 0;
    let endRow = this.SLICE_SIZE;
    const filterModel = this.drawingsGrid.api.getFilterModel();
    const gridModel: GridModel = new GridModel(startRow, endRow, sortModel, filterModel);

    this.fetchExportData(gridModel);
  }

  public toggleSelectedRows(change: boolean): void {
    if (change) {
      this.showSelectedItems = true;
      if (!AssertionUtils.isNullOrUndefined(this.drawingLibraryHelper)) {
        this.drawingLibraryHelper.savedFilterModel = null;
      }

      this.drawingsGrid.api.setFilterModel(null);

      const filterModel = this.drawingLibraryHelper.getPreselectedDrawingAndConfigurationIdsFilterModel(this.preselectedDesignIds, this.preselectedConfigurationIds);

      if (!AssertionUtils.isNullOrUndefined(this.drawingLibraryHelper)) {
        this.drawingLibraryHelper.savedFilterModel = filterModel;
      }

      this.drawingsGrid.api.setFilterModel(filterModel);
    } else {
      if (!AssertionUtils.isNullOrUndefined(this.drawingLibraryHelper)) {
        this.drawingLibraryHelper.savedFilterModel = null;
      }
      this.showSelectedItems = false;
      this.setInitializationFilters();
    }
  }

  public getTooltip(key: string): string {
    const translation = this.translate.instant(key);
    return translation.length > 20 ? translation : '';
  }

  private getStatusBarData(gridModel: GridModel): void {
    this.statusBarService
      .getStatusBarData(StatusBarTarget.DRAWING_LIBRARY, gridModel)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((statusBar: StatusBar) => (this.statusBar = statusBar));
  }

  private fetchExportData(gridModel: GridModel): void {
    this.drawings
      .getAllWithoutImageDataFromGridModel(TargetForListOfDrawingsEnum.DRAWING_LIBRARY, gridModel)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((listOfDrawings: Drawing[]) => {
        listOfDrawings.forEach((drawing: Drawing) => {
          this.exportData.push(drawing);
        });

        if (listOfDrawings.length < 29) {
          this.exportDrawingsGrid.api.setGridOption('rowData', this.exportData);
          this.exportGridOptions.cacheBlockSize = this.exportData.length;
          this.exportDrawingsGrid.api.exportDataAsCsv({columnKeys: ['name', 'widthInPx', 'heightInPx', 'shape', 'dateModified', 'sizeInKB']});
          this.isExporting = false;
          this.exportData = [];
          this.onExportComplete();
          this.dialogBuilder.close();
        } else {
          gridModel.startRow += this.SLICE_SIZE;
          gridModel.endRow += this.SLICE_SIZE;
          this.fetchExportData(gridModel);
        }
      });
  }

  private filterViewModes(currentSubscription: Subscription): void {
    if (!currentSubscription.hasPermission(Permission.TEXFAB_ORDERBOOK)) {
      this.viewModes = this.viewModes.filter(
        (viewMode: ProductionScheduleDrawingViewMode) => viewMode !== ProductionScheduleDrawingViewMode.ORDER_LINES && viewMode !== ProductionScheduleDrawingViewMode.GROUPED_ORDER_LINES
      );
    }
  }

  private getServerSideDatasource(): IServerSideDatasource {
    if (!this.readyToFetchRows) {
      return {
        getRows: (params: IServerSideGetRowsParams): void => {
          params.success({
            rowData: [],
            rowCount: 0
          });
        }
      };
    } else {
      return {
        getRows: (params: IServerSideGetRowsParams): void => {
          this.changingFilter = true;

          const endRow = params.request.endRow;
          const startRow = params.request.startRow;
          const sortModel = params.request.sortModel;
          let filterModel: any = params.request.filterModel;
          if (!AssertionUtils.isNullOrUndefined(this.drawingLibraryHelper)) {
            this.drawingLibraryHelper.savedFilterModel = filterModel;
          }

          this.drawingListGridModel = new GridModel(startRow, endRow, sortModel, filterModel);

          this.drawings
            .getAllWithoutImageDataFromGridModel(
              this.showConfigurations ? TargetForListOfDrawingsEnum.PRODUCTION_ORDER : TargetForListOfDrawingsEnum.DRAWING_LIBRARY,
              this.drawingListGridModel,
              this.showConfigurations
            )
            .pipe(takeUntil(this.unSubscribeOnViewDestroy))
            .subscribe({
              next: (listOfDrawings: Drawing[]) => {
                const drawings = this.getRowData(listOfDrawings);

                const endRowOrderbook = drawings.length < this.SLICE_SIZE ? startRow + drawings.length : undefined;

                if (startRow === 0) {
                  if (this.isProductionSchedule) {
                    this.getStatusBarData(this.drawingListGridModel);
                  }

                  if (drawings.length === 0) {
                    params.api.showNoRowsOverlay();
                  } else {
                    params.api.hideOverlay();
                  }
                }

                params.success({rowData: drawings, rowCount: endRowOrderbook});

                this.setSelectedDrawings(params.api);

                setTimeout(() => {
                  this.changingFilter = false;
                });
              },
              error: (error: any) => {
                params.fail();
                params.api.showNoRowsOverlay();
                throw error;
              }
            });
        }
      };
    }
  }

  private onExportComplete(): void {
    this.exportComplete.emit();
  }

  private getRowData(listOfDrawings: Drawing[]): Drawing[] | ListDrawingConfiguration[] {
    if (this.showConfigurations) {
      return listOfDrawings.map((drawing: Drawing) => new ListDrawingConfiguration(drawing, 0));
    }

    return listOfDrawings;
  }

  private instantiateGridOptionsForDrawings(): void {
    let defaultGridOptionsForDrawings = this.getDefaultGridOptionsForDrawings();

    if (this.isProductionSchedule && !this.isPreSelection) {
      defaultGridOptionsForDrawings = defaultGridOptionsForDrawings
        .withOnRowSelected((event: RowSelectedEvent) => {
          setTimeout(() => {
            if (this.processRowSelection && this.hasQuantitySuggestionPermission) {
              this.drawingLibraryHelper.setQuantitySuggestionForPath(event, this.productionSchedule, this.selectedPath, this.availableSpaceInBuggyInPicks, this.buggyLengthMaxInPicks);
            }
          }, 500);
        })
        .withRowDraggingOutsideGrid(
          (params: {rowNode: RowNode; dragEvent: DragEvent}) => this.drawingLibraryHelper.onRowDrag(params, this.productionScheduleItemMultiplier, this.draggingService, this.renderer),
          undefined,
          1
        );
    }

    if (this.isProductionSchedule) {
      this.gridOptionsForDrawings = defaultGridOptionsForDrawings
        .withSingleClickEdit()
        .withRowSelection(this.isPreSelection, false, false, !this.isPreSelection)
        .withOnCellEditingStarted((event: CellEditingStartedEvent) => this.onCellEditingStarted(event))
        .withOnRowSelected((event: RowSelectedEvent) => this.onRowSelection(event))
        .withOnGridReady((event: GridReadyEvent) => {
          event.api.refreshCells();
          event.api.refreshHeader();
          this.setInitializationFilters();
        })
        .withOnCellValueChanged((event: CellValueChangedEvent) => {
          this.addDrawingToProductionSchedulePath(event.colDef, event.data);
        })
        .withOnRowDoubleClicked((event: RowDoubleClickedEvent) => {
          this.processRowSelection = false;
          const colDef = this.getColumnDefinitionForClickedCell();
          this.addDrawingToProductionSchedulePath(colDef, event.data);
          setTimeout(() => {
            this.processRowSelection = true;
          }, 1000);
        })
        .withOnCellEditingStopped((event: CellEditingStoppedEvent) => {
          if (this.hasQuantitySuggestionPermission) {
            this.drawingLibraryHelper.recalculateQuantitySuggestionWhenRollLengthIsChanged(
              event,
              this.rollLengthColumnId,
              this.productionSchedule,
              this.selectedPath,
              this.buggyLengthMaxInPicks,
              this.availableSpaceInBuggyInPicks
            );
          }
        })
        .build();
    } else {
      this.gridOptionsForDrawings = defaultGridOptionsForDrawings
        .withRowSelection(false)
        .withOnRowSelected((event: RowSelectedEvent) => this.onRowSelection(event))
        .build();
    }
  }

  private getDefaultGridOptionsForDrawings(): GridOptionsBuilder {
    return this.gridOptionsBuilderFactoryService
      .getBuilder(this.createColumnsDefinition(), GridIdentifier.DESIGN_LIST_NEW)
      .withOnFilterChanged(({api}: FilterChangedEvent) => {
        const floatingFilters = Object.values(api.getFilterModel());
        if (this.floatingFilters !== floatingFilters) {
          this.floatingFilters = floatingFilters;
          this.syncAdvancedSearchFiltersWithFloatingFilters();
        }
      })
      .withGetRowHeight(() => {
        return this.designPreviewFiltered ? this.ROW_HEIGHT_WITHOUT_PREVIEW : this.ROW_HEIGHT_WITH_PREVIEW;
      })
      .withNoRowsOverlay(this.isPreSelection ? this.getNoRowsOverlayForPreselection() : this.getNoRowsOverlayForBuilder())
      .withRowClass(this.designPreviewFiltered ? HeaderClass.COMPACT.toString() : '')
      .withOnColumnVisible((event: ColumnVisibleEvent<any>) => this.withOnColumnVisible(event))
      .withServerSideDataSource(this.SLICE_SIZE, this.getServerSideDatasource())
      .withGetRowId((params: GetRowIdParams) => this.createRowIDForDrawing(this.showConfigurations ? params.data.drawing : params.data))
      .withLoadingOverlay()
      .withContext({componentParent: this});
  }

  private setInitializationFilters(): void {
    let filterModel: any;

    if (this.isProductionSchedule) {
      filterModel = this.drawingLibraryHelper.getInitializationFilterModel(
        this.showOnlyPreSelectedDrawings,
        this.productionSchedule,
        this.selectedPath,
        this.selectedRestPathColoredYarnSet,
        this.preselectedDesignIds,
        this.preselectedConfigurationIds,
        this.isPreSelection,
        this.selectedPathContentRowData
      );
    }

    this.readyToFetchRows = true;
    this.drawingsGrid?.api?.setFilterModel(filterModel);

    //because we fetched no rows yet, we need to explictly trigger a new getrows on aggrid
    this.drawingsGrid?.api?.setGridOption('serverSideDatasource', this.getServerSideDatasource());
  }

  private subscribeToOverlayActions(): void {
    this.overlayActionsService.actionTriggeredEmitter.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((actionKey: string) => {
      if (actionKey === this.noOverlayAction[0].key) {
        this.navigateBackOutput.emit();
      }
    });
  }

  private getNoRowsOverlayForPreselection(): NoDataOverlayComponentParams {
    return {
      scale: 0.7,
      titleParam: 'PRODUCTION_ORDER.DESIGN_LIBRARY.VIEW_MODES.DESIGNS',
      hideDescription: true,
      isAnyFilterPresent: () => this.hasActiveFilters()
    } as NoDataOverlayComponentParams;
  }

  private getNoRowsOverlayForBuilder(): NoDataOverlayComponentParams {
    return {
      scale: 0.4,
      hideDescription: false,
      titleKey: 'PRODUCTION_ORDER.WEAVE_PLAN.PRESELECTION.NO_ITEMS_SELECTED',
      descriptionKey: 'PRODUCTION_ORDER.WEAVE_PLAN.PRESELECTION.GO_TO_PRESELECTION',
      actions: this.noOverlayAction,
      hideActions: false
    } as NoDataOverlayComponentParams;
  }

  private withOnColumnVisible(event: ColumnVisibleEvent<any>): void {
    this.designPreviewFiltered = event.visible === false && event.columns.find((column: any) => column.colDef.cellClass === 'preview') != null;

    event.api.forEachNode((node: IRowNode) => {
      if (this.designPreviewFiltered) {
        node.setRowHeight(this.ROW_HEIGHT_WITHOUT_PREVIEW);
      } else {
        node.setRowHeight(this.ROW_HEIGHT_WITH_PREVIEW);
      }
    });

    event.api.onRowHeightChanged();

    this.gridOptionsForDrawings.headerHeight = this.designPreviewFiltered ? this.HEADER_HEIGHT_WITHOUT_PREVIEW : this.HEADER_HEIGHT_WITH_PREVIEW;
    this.gridOptionsForDrawings.floatingFiltersHeight = this.designPreviewFiltered ? this.HEADER_HEIGHT_WITHOUT_PREVIEW : this.HEADER_HEIGHT_WITH_PREVIEW;

    const colDefs = event.api.getColumnDefs();

    for (const colDef of colDefs) {
      colDef.headerClass = this.designPreviewFiltered ? HeaderClass.COMPACT.toString() : '';
    }

    event.api.setGridOption('columnDefs', colDefs);
    event.api.setGridOption('headerHeight', this.gridOptionsForDrawings.headerHeight);
    event.api.setGridOption('floatingFiltersHeight', this.gridOptionsForDrawings.floatingFiltersHeight);
    event.api.setGridOption('rowClass', this.designPreviewFiltered ? HeaderClass.COMPACT.toString() : '');

    event.api.refreshHeader();
    event.api.refreshCells({force: true});
  }

  private addDrawingToProductionSchedulePath(colDef: ColDef, data: ListDrawingConfiguration): void {
    if (colDef.headerName !== this.translate.instant(this.QUANTITY_TRANSLATION_KEY) && colDef.colId !== this.rollLengthColumnId) {
      const listDrawing = cloneDeep(data);
      listDrawing.amount = Math.floor(listDrawing.amount / this.productionScheduleItemMultiplier);

      if (
        !AssertionUtils.isNullOrUndefined(this.selectedPath) &&
        this.productionSchedule.canAddProductionScheduleItemToProductionSchedulePathForNewBuilder(listDrawing, this.selectedPath.technicalWidthInDents)
      ) {
        this.addDrawingToPath(listDrawing);
      } else if (
        AssertionUtils.isNullOrUndefined(this.selectedPath) &&
        this.productionSchedule.canAddProductionScheduleItemToColoredYarnSet(this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent, listDrawing)
      ) {
        this.addDrawingToNewPath(listDrawing);
      } else {
        this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
          titleText: 'PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN',
          messageText: 'PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.PATH_WIDTH_DOES_NOT_EQUAL_DESIGN_WIDTH',
          type: DialogType.INFORMATION
        });

        setTimeout(() => {
          this.addingItemToPath = false;
        }, 3000);
      }
    }
  }

  private getColumnDefinitionForClickedCell(): ColDef {
    const column = this.drawingsGrid.api.getAllDisplayedColumns().find((col: Column) => col.getColId() === this.drawingsGrid.api.getFocusedCell().column.getColId());
    return !AssertionUtils.isNullOrUndefined(column) ? column.getColDef() : null;
  }

  private addDrawingToPath(listDrawing: ListDrawingConfiguration): void {
    const productionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(
      this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent
    );

    this.productionSchedulePlan.addDrawingToProductionSchedulePath(listDrawing, this.selectedPath.pathNumber, productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet);
  }

  private addDrawingToNewPath(listDrawing: ListDrawingConfiguration): void {
    this.productionSchedulePlan
      .addDrawingToColoredYarnSet(listDrawing, this.productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet.startDent)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((path: ProductionSchedulePath) => {
        if (!AssertionUtils.isNullOrUndefined(path)) {
          this.selectedPath = path;
          this.selectedRestPathColoredYarnSet = undefined;
          this.selectedPathOutput.emit(this.selectedPath);
          this.selectedRestPathColoredYarnSetOutput.emit(this.selectedRestPathColoredYarnSet);
        }
      });
  }

  private setSelectedDrawings(gridApi: GridApi): void {
    gridApi.deselectAll();
    if (this.preselectedDesignIds?.length > 0) {
      gridApi?.forEachNode((node: RowNode<ListDrawingConfiguration>) => {
        if (!AssertionUtils.isNullOrUndefined(node.data)) {
          if (AssertionUtils.isNullOrUndefined(node.data.configurationId)) {
            node.setSelected(this.preselectedDesignIds.some((designId: number) => designId === node.data.drawing.id));
          } else {
            node.setSelected(this.preselectedConfigurationIds.some((configurationId: number) => configurationId === node.data.configurationId));
          }
        }
      });
    }
  }

  private hasActiveFilters(): boolean {
    return Object.values(this.drawingsGrid.api.getFilterModel()).length > 0;
  }

  private onRowSelection(event: RowSelectedEvent): void {
    if (this.isPreSelection && !this.changingFilter) {
      const selectedRows = event.api.getSelectedNodes();
      const selectedDesigns = selectedRows.map((rowNode: RowNode<ListDrawingConfiguration>) => rowNode.data);
      const drawingId = event?.data?.drawing?.id;
      const configurationId = event?.data?.configurationId;

      let finalDesignIdsList = this.preselectedDesignIds ?? [];
      let finalConfigurationIdsList = this.preselectedConfigurationIds ?? [];

      if (event.node.isSelected() === false) {
        // Find and remove only the first occurrence of `idToRemove` in `finalDesignIdsList`
        const designIndex = finalDesignIdsList.indexOf(drawingId);
        if (designIndex !== -1) {
          finalDesignIdsList.splice(designIndex, 1);
        }

        // Find and remove only the first occurrence of `configurationIdToRemove` in `finalConfigurationIdsList`
        const configurationIndex = finalConfigurationIdsList.indexOf(configurationId);
        if (configurationIndex !== -1) {
          finalConfigurationIdsList.splice(configurationIndex, 1);
        }

        this.preselectedDesignIds = finalDesignIdsList;
        this.preselectedConfigurationIds = finalConfigurationIdsList;
      } else {
        this.preselectedDesignIds.push(drawingId);
        if (!AssertionUtils.isNullOrUndefined(configurationId) && configurationId !== 0) {
          this.preselectedConfigurationIds.push(configurationId);
        }
      }

      this.drawingSelected.emit(selectedDesigns);
      this.drawingIdSelected.emit(this.preselectedDesignIds);
      this.configurationIdSelected.emit(this.preselectedConfigurationIds);
    } else {
      const selectedRows = event.api.getSelectedNodes();
      const selectedDesigns = selectedRows.map((rowNode: RowNode<ListDrawingConfiguration>) => rowNode.data);
      this.drawingSelected.emit(selectedDesigns);
    }
  }

  private onCellEditingStarted(event: CellEditingStartedEvent): void {
    event.node.setSelected(!event.node.isSelected());
  }

  private createColumnsDefinition(): ColDef[] {
    const drawingPropertyPrefix = this.showConfigurations ? 'drawing.' : '';
    const alignRightString = 'align-right';

    let nameColDef = this.colDefBuilderFactory
      .getBuilder()
      .withColId('name')
      .withField(`${drawingPropertyPrefix}name`, true)
      .withHeaderName(this.DESIGN_NAME_TRANSLATION_KEY)
      .withComparator(StringUtils.stringComparator)
      .withValueGetter((params: ValueGetterParams<ListDrawingConfiguration>) => [{id: 1, name: params.data.drawing?.name} as IdName])
      .withCellRenderer(InputChipCellRendererComponent, (params: ICellRendererParams) => {
        return {compact: true, clickable: true, onChipClick: (): any => this.openDesignDetail(params.data)} as InputChipCellRendererParams;
      })
      .withTextMultiFilter(this.getPossibleColumnsValues('name'));

    if (!this.isProductionSchedule || this.isPreSelection) {
      nameColDef = nameColDef.withLockVisible().withSuppressMovable();
    }

    const defaultColDefs = [
      this.colDefBuilderFactory
        .getBuilder()
        .withColIdAndField('id')
        .withValueGetter((params: ValueGetterParams) => this.createRowIDForDrawing(this.showConfigurations ? params.data.drawing : params.data))
        .withNumericMultiFilter(this.getPossibleColumnsValues('id'))
        .withHide()
        .withHiddenInToolPanel()
        .build(),
      this.colDefBuilderFactory.getBuilder().withColIdAndField('configurationId').withNumericMultiFilter(this.getPossibleColumnsValues('configurationId')).withHiddenInToolPanel().withHide().build(),
      nameColDef.build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('preview')
        .withValueGetter((params: ValueGetterParams) => (this.showConfigurations ? params.data.drawing : params.data))
        .withHeaderName('GENERAL.PREVIEW')
        .withCellRenderer(DesignPreviewComponent, null, true)
        .withCellClass('preview')
        .withSortable(false)
        .withMinWidth(128)
        .withMaxWidth(128)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('qualityName')
        .withField(`quality.name`, true)
        .withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('qualityName'))
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('colorSetName')
        .withField(`colorSet.name`, true)
        .withHeaderName('TEXTILE_DATA.COLOR_SET.COLOR_SET')
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('colorSetName'))
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('mainColor')
        .withField('mainColor.name', true)
        .withComparator(StringUtils.stringComparator)
        .withHeaderName('GENERAL.ADVANCED_SEARCH.MAIN_COLOR')
        .withTextMultiFilter(this.getPossibleColumnsValues('mainColor'))
        .withCellRendererSelector(this.cellRendererSelector(GridCreelPreviewOfCreelPositionComponent, {previewType: PreviewType.COLOR, showRgbValues: true}, true))
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('borderColor')
        .withField('borderColor.name', true)
        .withComparator(StringUtils.stringComparator)
        .withHeaderName('GENERAL.ADVANCED_SEARCH.BORDER_COLOR')
        .withCellRendererSelector(this.cellRendererSelector(GridCreelPreviewOfCreelPositionComponent, {previewType: PreviewType.COLOR, showRgbValues: true}, true, false))
        .withTextMultiFilter(this.getPossibleColumnsValues('borderColor'))
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('commercialWidthInCM')
        .withField(`commercialDimensionsInCm.widthInCM`)
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH', 1, false, Unit.CENTIMETER)
        .withValueFormatter((params: ValueFormatterParams) => params.data.commercialDimensionsInCM?.toFixed(2))
        .withNumericMultiFilter(this.getPossibleColumnsValues('commercialWidthInCM'))
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('widthInPx')
        .withField(`${drawingPropertyPrefix}dimensionsInPixels.widthInPx`)
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH', 1, false, Unit.PIXEL)
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.PIXEL, this.l10nIntlService))
        .withCellClass(alignRightString)
        .withNumericMultiFilter(this.getPossibleColumnsValues('widthInPx'))
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('heightInPx')
        .withField(`${drawingPropertyPrefix}dimensionsInPixels.heightInPx`)
        .withHeaderName('GENERAL.DIMENSIONS.LENGTH', 1, false, Unit.PIXEL)
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.PIXEL, this.l10nIntlService))
        .withCellClass(alignRightString)
        .withNumericMultiFilter(this.getPossibleColumnsValues('heightInPx'))
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('shape')
        .withField(`${drawingPropertyPrefix}shape`)
        .withHeaderName('GENERAL.SHAPE')
        .withValueGetter((params: ValueGetterParams) => this.getShape(params.data), true)
        .withComparator(StringUtils.stringComparator)
        .withTextMultiFilter(this.getPossibleColumnsValues('shape'))
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('dateModified')
        .withField(`${drawingPropertyPrefix}dateModified`)
        .withHeaderName('TEXTILE_DATA.LAST_MODIFIED')
        .withDate()
        .withDateMultiFilter(this.getPossibleColumnsValues('dateModified'))
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('sizeInKB')
        .withField(`${drawingPropertyPrefix}sizeInKB`)
        .withHeaderName('DESIGN_LIBRARY.DETAILS.SIZE')
        .withCellClass(alignRightString)
        .withValueGetter((params: ValueGetterParams) => this.getSizeInKB(params.data))
        .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithUnit(params.getValue(), Unit.KILOBYTE, this.l10nIntlService), null, true)
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.KILOBYTE, this.l10nIntlService))
        .withRightAlignment()
        .withoutFilter()
        .withSortable()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColIdAndField('drawingType')
        .withValueGetter((params: ValueGetterParams) => (this.showConfigurations ? params.data.drawingType : params.data))
        .withTextMultiFilter(of(['BMP', 'EP']))
        .withHide(!AssertionUtils.isNullOrUndefined(this.filterOnDrawingType))
        .withHiddenInToolPanel()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColIdAndField('missingDesign')
        .withValueGetter((params: ValueGetterParams<any>) => (this.showConfigurations ? params.data.drawing.missingDesign : params.data))
        .withTextMultiFilter(of(['true', 'false']))
        .withHide()
        .withHiddenInToolPanel()
        .build()
    ];

    const builderAmountColDef = [
      this.colDefBuilderFactory
        .getBuilder()
        .withColIdAndField('add')
        .withHeaderName('PRODUCTION_ORDER.DESIGN_LIBRARY.ORDER_LINES.ADD_TO_PATH')
        .withCellRenderer(ProgressCellRendererComponent, {label: 'GENERAL.ACTIONS.ADD', isSaving: this.addingItemToPath}, true)
        .withoutFilter()
        .withSuppressMovable()
        .withMinWidth(135)
        .withMaxWidth(200)
        .withHide(!this.isProductionSchedule || this.isPreSelection)
        .withHiddenInToolPanel()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('quantity')
        .withField('amount')
        .withHeaderName(this.QUANTITY_TRANSLATION_KEY)
        .withOnCellClicked((event: CellClickedEvent) => this.editQuantityCell(event))
        .withSortable(false)
        .withEditable(true)
        .withCellEditor(NumberCellEditorComponent, (): NumberCellEditorParams => ({min: this.productionScheduleItemMultiplier, isSmall: false, autoSelect: true}) as NumberCellEditorParams)
        .withValueGetter((params: ValueGetterParams<ListDrawingConfiguration>) => {
          if (params.data?.amount) {
            return params.data.amount;
          } else {
            return this.drawingLibraryHelper.getQuantitySuggestionForPath(
              params.data,
              this.productionSchedule,
              this.selectedPath,
              this.availableSpaceInBuggyInPicks,
              this.buggyLengthMaxInPicks,
              this.hasQuantitySuggestionPermission,
              this.productionScheduleItemMultiplier
            );
          }
        })
        .withValueSetter((params: ValueSetterParams<ListDrawingConfiguration>) => this.setQuantity(params))
        .withMinWidth(100)
        .withoutFilter()
        .withLockVisible()
        .withSuppressMovable()
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId(this.rollLengthColumnId)
        .withField(`rollLengthInMeter`)
        .withHeaderName('PRODUCTION_ORDER.WEAVE_PLAN.PATH_LAYOUT.ROLL_LENGTH', undefined, false, Unit.METER)
        .withOnCellClicked((event: CellClickedEvent) => this.editQuantityCell(event))
        .withSortable(false)
        .withEditable((params: EditableCallbackParams<ListDrawingConfiguration>) => this.isRollLengthEditable(params))
        .withCellEditor(NumberCellEditorComponent, (): NumberCellEditorParams => ({isSmall: false, autoSelect: true}) as NumberCellEditorParams)
        .withValueGetter((params: ValueGetterParams<ListDrawingConfiguration>) => {
          if (params.data?.rollLengthInMeter && params.data?.commercialDimensionsInCm?.heightInCM / 100 < params.data.rollLengthInMeter) {
            return params.data.rollLengthInMeter;
          } else if (params.data?.drawing?.repeated) {
            const rollLengthInMeter = params.data?.commercialDimensionsInCm?.heightInCM / 100;
            if (!AssertionUtils.isNullOrUndefined(rollLengthInMeter) && !isNaN(rollLengthInMeter)) {
              params.data.rollLengthInMeter = rollLengthInMeter;
              return params.data.rollLengthInMeter;
            } else {
              return convertPixelsToHeightInMeter(
                params?.data?.drawing?.dimensionsInPixels?.heightInPx,
                this.productionSchedule?.machineQuality?.pickDensity * 1000,
                this.productionSchedule?.machineQuality?.weaveStructure?.picksPerColorLine
              );
            }
          } else {
            return null;
          }
        })
        .withMinWidth(130)
        .withoutFilter()
        .withHide(this.isPreSelection)
        .withHiddenInToolPanel(this.isPreSelection)
        .withRightAlignment()
        .build(),
      this.colDefBuilderFactory
        .getBuilder()
        .withColId('heightInCM')
        .withField(`commercialDimensionsInCm.heightInCM`)
        .withHeaderName('GENERAL.DIMENSIONS.HEIGHT', 1, false, Unit.CENTIMETER)
        .withNumericMultiFilter(this.getPossibleColumnsValues('heightInCM'))
        .withRightAlignment()
        .withHiddenInToolPanel()
        .withHide(this.isPreSelection)
        .build()
    ];

    return this.isProductionSchedule && !this.isPreSelection ? [...builderAmountColDef, ...defaultColDefs] : defaultColDefs;
  }

  private openDesignDetail(data: ListDrawingConfiguration): void {
    this.navigationHelper.navigateWithContentSwitcher({
      componentInfo: {
        component: DrawingLibraryDetailsComponent,
        componentInputs: {
          drawingId: data.drawing.id,
          canShowFooter: !this.isProductionSchedule,
          disableAllFields: this.isProductionSchedule,
          scrollToConfiguration: this.isProductionSchedule,
          productionScheduleDrawing: data
        } as DrawingLibraryDetailsForProductionScheduleData,
        module: DrawingLibraryDetailsModule
      },
      dialogWidth: 'medium',
      canShowBMSTheme: false
    });
  }

  private getShape(drawing: Drawing | ListDrawingConfiguration): string {
    const shape = drawing instanceof ListDrawingConfiguration ? drawing.drawing.shape : drawing.shape;
    return FixedShape.getTranslationKeyForShape(shape, this.translate);
  }

  private getSizeInKB(drawing: Drawing | ListDrawingConfiguration): number {
    if (drawing instanceof ListDrawingConfiguration) {
      return drawing.drawing.sizeInKB ? Math.round(drawing.drawing.sizeInKB) : 0;
    }
    return drawing.sizeInKB ? Math.round(drawing.sizeInKB) : 0;
  }

  private editQuantityCell(event: CellClickedEvent): void {
    if (this.drawingsGrid?.api) {
      this.drawingsGrid?.api?.stopEditing();
    }

    if (event.column.getColDef().editable) {
      this.drawingsGrid?.api.startEditingCell({
        rowIndex: event?.rowIndex,
        colKey: event?.column?.getColId()
      });
    }
  }

  private setQuantity(params: ValueSetterParams<ListDrawingConfiguration>): boolean {
    if (this.productionScheduleItemMultiplier === 2 && params.newValue % 2 !== 0) {
      params.newValue--;
    }
    params.data.amount = Math.max(this.productionScheduleItemMultiplier, params.newValue);
    return true;
  }

  private createRowIDForDrawing(drawing: Drawing): string {
    return AssertionUtils.isNullOrUndefined(drawing) ? `${UuidUtils.generateV4Uuid()}` : `${drawing.id}${UuidUtils.generateV4Uuid()}`;
  }

  private syncAdvancedSearchFiltersWithFloatingFilters(): void {
    const advancedSearchFiltersFromFloatingFilters = this.advancedSearchFilters.map((advancedSearchFilter: PropertyValue) => {
      const floatingFilterToSyncWith = this.floatingFilters.find((floatingFilter: PropertyValue) => floatingFilter.propertyName === advancedSearchFilter.propertyName);
      if (floatingFilterToSyncWith !== undefined) {
        advancedSearchFilter.propertyValue = floatingFilterToSyncWith.propertyValue;
      }
      return advancedSearchFilter;
    });
    this.advancedSearchFiltersChange.emit(advancedSearchFiltersFromFloatingFilters);
  }

  private cellRendererSelector<TData = any, TValue = any>(
    cellRenderer: any = null,
    cellRendererParams: any = null,
    cellForMainOrBorderColor: boolean = false,
    useMainColor: boolean = true
  ): (params: ICellRendererParams<TData, TValue>) => CellRendererSelectorResult {
    return (params: ICellRendererParams<TData, TValue>): CellRendererSelectorResult => {
      if (cellForMainOrBorderColor) {
        const configuration = params.data as ListDrawingConfiguration;
        cellRendererParams.value = {creelPositionForColors: useMainColor ? configuration.mainColor : configuration.borderColor};
      }
      return params.node.level === 0 ? {component: cellRenderer ?? params.colDef.cellRenderer, params: cellRendererParams} : {component: EmptyCellRendererComponent};
    };
  }

  private isRollLengthEditable(params: EditableCallbackParams<ListDrawingConfiguration>): boolean {
    return params.data.drawing?.repeated;
  }

  private getPossibleColumnsValues(columnIdentifier: string): Observable<string[]> {
    return this.drawings.getPossibleValues(columnIdentifier).pipe(takeUntil(this.unSubscribeOnViewDestroy));
  }
}
